import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import { sheetSpacing } from '../design/tokens';
import SingleLineText from './ui/SingleLineText';
import LoadingLabel from './LoadingLabel';
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import BottomSheet from './BottomSheet';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { FONT_FAMILY as FONT_STACK } from '../design/tokens';
const PRESETS = [15, 30, 60, 120];
const PRESET_LABELS = { 15: translationKey('15분 뒤'), 30: translationKey('30분 뒤'), 60: translationKey('1시간 뒤'), 120: translationKey('2시간 뒤') };

// PENDING(복용) 카드 톤 — 노랑/베이지 팔레트
const C = {
    cardBg: '#fffbeb',
    cardBorder: '#fde68a',
    accent: '#8a7000',
    primary: '#ffe566',
    primaryText: '#5a4a00',
    primaryActive: '#e8d040',
    chipBorder: '#EDE8DD',
    chipBg: '#fff',
    chipText: '#1F1D1B',
    cancelBorder: '#EDE8DD',
    cancelText: '#7A766F',
    subText: '#7A766F',
    titleText: '#1F1D1B',
    handle: '#E2DDD2',
};

const pad2 = (n) => String(n).padStart(2, '0');
const formatHHMM = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const formatTime = (d) => {
    const language = readAppLanguage();
    if (language !== 'ko') return new Intl.DateTimeFormat(language, { hour: 'numeric', minute: '2-digit', hour12: language === 'en' }).format(d);
    const hour = d.getHours();
    const minute = d.getMinutes();
    const hourLabel = localize("{v0} {v1}시", { v0: hour < 12 ? localize('오전') : localize('오후'), v1: hour % 12 || 12 });
    return minute === 0 ? hourLabel : localize("{v0} {v1}분", { v0: hourLabel, v1: minute });
};

export default function SnoozeSheet({
    open, medName, medCount, targets = [], now,
    defaultDelayMin = 30,
    onClose, onConfirm,
}) {
    const [mode, setMode] = useState('preset');
    const [delayMin, setDelayMin] = useState(defaultDelayMin);
    const [customTime, setCustomTime] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const titleId = 'snooze-sheet-title';
    const firstChipRef = useRef(null);
    const submittingRef = useRef(false);

    useLayoutEffect(() => {
        if (open) {
            setMode('preset');
            setDelayMin(defaultDelayMin);
            setCustomTime(null);
            setSubmitting(false);
            submittingRef.current = false;
        }
    }, [open, defaultDelayMin]);

    useEffect(() => {
        if (open && firstChipRef.current) firstChipRef.current.focus();
    }, [open]);
    const snoozeUntil = useMemo(() => {
        if (!now) return null;
        if (mode === 'preset') {
            return new Date(now.getTime() + delayMin * 60 * 1000);
        }
        if (!customTime) return null;
        const [h, m] = customTime.split(':').map(Number);
        const target = new Date(now);
        target.setSeconds(0, 0);
        target.setMilliseconds(0);
        target.setHours(h, m);
        if (target.getTime() <= now.getTime()) {
            target.setDate(target.getDate() + 1);
        }
        return target;
    }, [mode, delayMin, customTime, now]);

    const diffLabel = useMemo(() => {
        if (!snoozeUntil || !now) return '';
        const diffMs = snoozeUntil.getTime() - now.getTime();
        const diffMin = Math.max(0, Math.round(diffMs / 60000));
        if (diffMin < 60) return localize("지금부터 {v0}분 후", { v0: diffMin });
        const h = Math.floor(diffMin / 60);
        const m = diffMin % 60;
        return m === 0 ? localize("지금부터 {v0}시간 후", { v0: h }) : localize("지금부터 {v0}시간 {v1}분 후", { v0: h, v1: m });
    }, [snoozeUntil, now]);

    const isBulk = (medCount || 0) > 1;
    const titleText = isBulk ? localize('나중에 알림 한 번에 설정') : localize('나중에 알림');
    const isTomorrow = snoozeUntil && now && (
        snoozeUntil.getFullYear() !== now.getFullYear() ||
        snoozeUntil.getMonth() !== now.getMonth() ||
        snoozeUntil.getDate() !== now.getDate()
    );
    const displayTime = snoozeUntil ? formatTime(snoozeUntil) : '--:--';
    const inputTime = snoozeUntil ? formatHHMM(snoozeUntil) : '';
    const subLine = snoozeUntil ? `${isTomorrow ? localize('내일') : localize('오늘')} · ${diffLabel}` : '';
    const canConfirm = !!snoozeUntil && !submitting;

    const handleCustomChange = (newTime) => {
        if (!newTime) return;
        setMode('custom');
        setCustomTime(newTime);
    };

    const handleSubmit = async () => {
        if (!canConfirm || submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        try {
            await onConfirm?.(snoozeUntil);
            onClose();
        } catch (_) {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    return (
        <BottomSheet
            dismissDisabled={submitting}
            open={open}
            onClose={onClose}
            ariaLabelledBy={titleId}
            borderRadius="22px 22px 0 0"
            handleColor={C.handle}
            px="20px"
            footer={(
                <Box sx={{ display: 'flex', gap: '8px', fontFamily: FONT_STACK }}>
                    <Box component="button" type="button" onClick={onClose} disabled={submitting}
                        sx={{
                            flex: 1, minWidth: 0, minHeight: 48,
                            border: `1px solid ${C.cancelBorder}`, bgcolor: '#fff', color: C.cancelText,
                            fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: '8px', borderRadius: '12px', cursor: 'pointer',
                            '&:active': { bgcolor: '#F8F5EE' },
                        }}>
                        <SingleLineText component="span" sx={{ display: 'block', fontSize: '1rem', fontWeight: 700 }}>{localize("취소")}</SingleLineText>
                    </Box>
                    <Box component="button" type="button" disabled={!canConfirm} onClick={handleSubmit}
                        sx={{
                            flex: 2, minWidth: 0, minHeight: 48, border: 'none',
                            bgcolor: canConfirm ? C.primary : '#E5DFC9', color: C.primaryText,
                            fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: '8px', borderRadius: '12px',
                            cursor: canConfirm ? 'pointer' : 'not-allowed',
                            boxShadow: canConfirm ? '0 2px 8px rgba(184,159,0,0.18)' : 'none',
                            '&:active': canConfirm ? { bgcolor: C.primaryActive } : {},
                        }}>
                        {submitting ? <LoadingLabel /> : <SingleLineText component="span" sx={{ display: 'block', fontSize: '1.1rem', fontWeight: 700 }}>{isBulk ? localize("{v0}개 모두 설정", { v0: medCount }) : readAppLanguage() === 'ko' ? localize('설정') : localize('이 시간으로 알림 설정')}</SingleLineText>}
                    </Box>
                </Box>
            )}
        >
            <Box role="region" aria-labelledby={titleId} sx={{ fontFamily: FONT_STACK }}>
                <Box sx={{ mb: sheetSpacing.section }}>
                    <SingleLineText component="h2" id={titleId}
                        sx={{ fontSize: '1.2rem', fontWeight: 800, letterSpacing: '-0.01em', color: C.titleText, fontFamily: FONT_STACK }}>
                        {titleText}
                    </SingleLineText>
                    <Typography sx={{ mt: '4px', fontSize: '0.95rem', color: C.subText, lineHeight: 1.45, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
                        {isBulk ? localize("{v0}개 약에 함께 적용해요", { v0: medCount }) : (medName || targets[0]?.name)}
                    </Typography>
                </Box>

                <Box component="fieldset" disabled={submitting} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
                    {/* The input receives the original gesture across the whole card. */}
                    <Box sx={{
                        position: 'relative', minWidth: 0, minHeight: 66,
                        bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: 3,
                        py: 1, px: 1.25, mb: '12px', cursor: 'pointer',
                        '&:active': { bgcolor: '#FFF7D6' },
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                            <SingleLineText sx={{ fontSize: '0.82rem', color: C.accent, fontWeight: 800, lineHeight: 1.2 }}>{localize("다시 알림 받을 시간")}</SingleLineText>
                            <ChevronRightIcon sx={{ fontSize: 17, color: '#B5A76A', flexShrink: 0 }} />
                        </Box>
                        <SingleLineText sx={{
                            mt: 0.7, fontSize: '1.1rem', fontWeight: 850, lineHeight: 1.4,
                            color: C.primaryText, fontVariantNumeric: 'tabular-nums', fontFamily: FONT_STACK,
                        }}>{displayTime}</SingleLineText>
                        <Box component="input" type="time" value={inputTime}
                            onChange={(e) => handleCustomChange(e.target.value)}
                            onClick={(e) => {
                                e.stopPropagation();
                                try {
                                    if (typeof e.currentTarget.showPicker === 'function') {
                                        e.currentTarget.showPicker();
                                        return;
                                    }
                                } catch (_) {}
                                try { e.currentTarget.focus(); } catch (_) {}
                            }}
                            aria-label={localize("다시 알림 시각 직접 선택")}
                            sx={{
                                position: 'absolute', inset: 0, width: '100%', height: '100%',
                                zIndex: 1, opacity: 0, cursor: 'pointer',
                                border: 'none', background: 'transparent', fontSize: 16, color: 'transparent',
                                '&::-webkit-calendar-picker-indicator': { width: '100%', height: '100%', cursor: 'pointer' },
                            }} />
                    </Box>
                    <Box sx={{ display: readAppLanguage() === 'ko' ? 'flex' : 'grid', ...(readAppLanguage() !== 'ko' && { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }), gap: '6px', flexWrap: 'nowrap' }}>
                        {PRESETS.map((min, i) => {
                            const isActive = mode === 'preset' && delayMin === min;
                            return (
                                <Box key={min} ref={i === 0 ? firstChipRef : null}
                                    component="button" type="button" aria-pressed={isActive} disabled={submitting}
                                    onClick={() => { setMode('preset'); setDelayMin(min); }}
                                    sx={{
                                        flex: '1 1 0', minWidth: 0, minHeight: 44,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        border: isActive ? '1px solid transparent' : `1px solid ${C.chipBorder}`,
                                        bgcolor: isActive ? C.primary : C.chipBg,
                                        color: isActive ? C.primaryText : C.chipText,
                                        fontFamily: FONT_STACK, py: '8px', px: '8px', borderRadius: 999, cursor: 'pointer',
                                        fontVariantNumeric: 'tabular-nums',
                                        '&:active': isActive ? { bgcolor: C.primaryActive } : { bgcolor: '#F8F5EE' },
                                    }}>
                                    <SingleLineText component="span" sx={{ display: 'block', fontSize: '0.9rem', fontWeight: 600 }}>{localize(PRESET_LABELS[min])}</SingleLineText>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
                {subLine && <Box sx={{ mt: '12px', ml: '4px' }}>
                    <SingleLineText sx={{ fontSize: '0.9rem', fontWeight: isTomorrow ? 700 : 600, color: isTomorrow ? C.primaryText : C.subText, lineHeight: 1.5 }}>
                        {subLine}
                    </SingleLineText>
                </Box>}

            </Box>
        </BottomSheet>
    );
}
